import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AuthShell } from "../auth/AuthShell";
import { config } from "@/config";
import { getIdToken } from "@/lib/firebase";
import { timeAgo } from "@/lib/normalize";

/** Suggestions board — entitled users post issues/ideas, vote and comment (reddit-ish).
 *  Status labels are admin-set and double as the public roadmap. */

interface Sugg {
  id: number; handle: string; title: string; body: string; category: string;
  status: string; at: number; votes: number; comments: number; my_vote: boolean | null;
}
interface Comment { id: number; handle: string; body: string; at: number }

const CATS = ["feature", "bug", "improvement", "other"] as const;
const STATUSES = ["open", "planned", "building", "shipped", "declined"] as const;

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const token = await getIdToken();
  const r = await fetch(`${config.restUrl}${path}`, {
    ...init,
    headers: { Authorization: `Bearer ${token}`, "content-type": "application/json", ...(init?.headers ?? {}) },
  });
  const j = (await r.json().catch(() => ({}))) as T & { error?: string };
  if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
  return j;
}

export function SuggestionsScreen() {
  const [sort, setSort] = useState<"top" | "new">("top");
  const [list, setList] = useState<Sugg[] | null>(null);
  const [admin, setAdmin] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [composing, setComposing] = useState(false);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let alive = true;
    api<{ suggestions: Sugg[]; admin: boolean }>(`/api/suggestions?sort=${sort}`)
      .then((j) => { if (alive) { setList(j.suggestions); setAdmin(j.admin); } })
      .catch((e) => { if (alive) setErr(e instanceof Error ? e.message : "failed to load"); });
    return () => { alive = false; };
  }, [sort, reload]);

  return (
    <AuthShell title="SUGGESTIONS" subtitle="You vote, we build — the board is the roadmap">
      <div className="sg-bar">
        <div className="sg-sort">
          <button className={`fb-chip${sort === "top" ? " on" : ""}`} onClick={() => setSort("top")}>TOP</button>
          <button className={`fb-chip${sort === "new" ? " on" : ""}`} onClick={() => setSort("new")}>NEW</button>
        </div>
        <button className="auth-btn primary" style={{ padding: "8px 14px" }} onClick={() => setComposing((v) => !v)}>
          {composing ? "CANCEL" : "+ NEW SUGGESTION"}
        </button>
      </div>

      {composing && <Composer onDone={() => { setComposing(false); setReload((r) => r + 1); }} />}

      {err && <div className="auth-error">{err}</div>}
      {!list && !err && <p className="auth-copy">Loading the board…</p>}
      {list && list.length === 0 && <p className="auth-copy">Nothing here yet — yours goes first.</p>}

      <div className="sg-list">
        {list?.map((s) => (
          <SuggRow key={s.id} s={s} admin={admin} onChanged={() => setReload((r) => r + 1)} />
        ))}
      </div>

      <div className="acct-actions" style={{ marginTop: 18 }}>
        <Link className="auth-btn" to="/ops">← BACK TO OPS</Link>
      </div>
    </AuthShell>
  );
}

function Composer({ onDone }: { onDone: () => void }) {
  const [category, setCategory] = useState<string>("feature");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit() {
    setBusy(true);
    setErr(null);
    try {
      await api<{ id: number }>("/api/suggestions", {
        method: "POST",
        body: JSON.stringify({ title: title.trim(), body: body.trim(), category }),
      });
      onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "failed to post");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="sg-compose">
      <div className="fb-row">
        {CATS.map((c) => (
          <button key={c} className={`fb-chip${category === c ? " on" : ""}`} onClick={() => setCategory(c)}>{c}</button>
        ))}
      </div>
      <input className="sg-title" maxLength={120} placeholder="One-line title" value={title} onChange={(e) => setTitle(e.target.value)} />
      <textarea className="fb-text" maxLength={2000} placeholder="What, and why it matters on a trading desk…" value={body} onChange={(e) => setBody(e.target.value)} />
      {err && <div className="auth-error">{err}</div>}
      <button className="auth-btn primary" disabled={busy || !title.trim() || !body.trim()} onClick={() => void submit()}>
        {busy ? "POSTING…" : "POST TO THE BOARD"}
      </button>
    </div>
  );
}

function SuggRow({ s, admin, onChanged }: { s: Sugg; admin: boolean; onChanged: () => void }) {
  const [votes, setVotes] = useState(s.votes);
  const [mine, setMine] = useState(Boolean(s.my_vote));
  const [open, setOpen] = useState(false);
  const [comments, setComments] = useState<Comment[] | null>(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { setVotes(s.votes); setMine(Boolean(s.my_vote)); }, [s.votes, s.my_vote]);

  async function toggleVote() {
    try {
      const j = await api<{ votes: number; myVote: boolean }>(`/api/suggestions/${s.id}/vote`, { method: "POST" });
      setVotes(j.votes);
      setMine(j.myVote);
    } catch { /* ignore */ }
  }

  async function toggleOpen() {
    const next = !open;
    setOpen(next);
    if (next && comments === null) {
      try {
        const j = await api<{ comments: Comment[] }>(`/api/suggestions/${s.id}`);
        setComments(j.comments);
      } catch { setComments([]); }
    }
  }

  async function postComment() {
    if (!draft.trim()) return;
    setBusy(true);
    try {
      const j = await api<{ comment: Comment }>(`/api/suggestions/${s.id}/comments`, {
        method: "POST",
        body: JSON.stringify({ body: draft.trim() }),
      });
      setComments((prev) => [...(prev ?? []), j.comment]);
      setDraft("");
    } catch { /* rate limit etc — keep draft */ } finally { setBusy(false); }
  }

  async function setStatus(status: string) {
    try {
      await api(`/api/suggestions/${s.id}`, { method: "PATCH", body: JSON.stringify({ status }) });
      onChanged();
    } catch { /* ignore */ }
  }

  return (
    <div className={`sg-row${open ? " open" : ""}`}>
      <button className={`sg-vote${mine ? " on" : ""}`} title={mine ? "Remove your vote" : "Upvote"} onClick={() => void toggleVote()}>
        <span className="tri">▲</span>
        <span className="n">{votes}</span>
      </button>
      <div className="sg-main" onClick={() => void toggleOpen()}>
        <div className="sg-top">
          <span className="sg-title-txt">{s.title}</span>
          <span className={`sg-status ${s.status}`}>{s.status.toUpperCase()}</span>
          <span className="sg-cat">{s.category}</span>
        </div>
        <div className="sg-by">{s.handle} · {timeAgo(s.at)} · {s.comments} comment{s.comments === 1 ? "" : "s"}</div>
        {open && (
          <div className="sg-detail" onClick={(e) => e.stopPropagation()}>
            <p className="sg-body">{s.body}</p>
            {admin && (
              <div className="fb-row" style={{ marginBottom: 8 }}>
                {STATUSES.map((st) => (
                  <button key={st} className={`fb-chip${s.status === st ? " on" : ""}`} onClick={() => void setStatus(st)}>{st}</button>
                ))}
              </div>
            )}
            <div className="sg-comments">
              {comments === null && <span className="sg-by">loading comments…</span>}
              {comments?.map((cm) => (
                <div className="sg-comment" key={cm.id}>
                  <span className="ch">{cm.handle}</span>
                  <span className="cb">{cm.body}</span>
                  <span className="ct">{timeAgo(cm.at)}</span>
                </div>
              ))}
            </div>
            <div className="cinput" style={{ padding: 0, border: 0 }}>
              <input value={draft} maxLength={1000} placeholder="add a comment…" onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") void postComment(); }} />
              <button disabled={busy || !draft.trim()} onClick={() => void postComment()}>SEND</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
